import { ApprovedOpenReservationFilter, Company, Reservation, Settings, Table, TableCombination } from '@backend/domain'
import {
	CompanyRepository,
	ReservationRepository,
	SettingsRepository,
	TableCombinationRepository,
	TableRepository,
} from '@backend/repository'
import {
	Availability,
	AvailabilityFeed,
	FeedMetadata_ProcessingInstruction,
} from '@core/google-reservations/types/feeds'
import { DateUtils, getTimeSlotsForWeek } from '@core/utils'
import { Processor, WorkerHost } from '@nestjs/bullmq'
import { Logger } from '@nestjs/common'
import { addDays, addMinutes, addWeeks, areIntervalsOverlapping, getUnixTime } from 'date-fns'
import { range } from 'es-toolkit'
import { promisify } from 'node:util'
import { gzip } from 'node:zlib'
import SFTPClient from 'ssh2-sftp-client'
import { In } from 'typeorm'
import { TracedProcessor } from '../../../../../../common'
import { GOOGLE, Stage } from '../../../../../../config'
import { GOOGLE_RESERVATIONS_QUEUES, UploadAvailabilityFeedJob } from '../../../../../../core'
import { ScheduleSettings } from '../../../../../settings'
import { getAllPartySizes, getOpenTableCount } from '../../../../utils'
import { DEFAULT_SLOT_LENGTH } from '../../constants'
import { getServiceId } from '../../utils'

const gzipAsync = promisify(gzip)

@Processor(GOOGLE_RESERVATIONS_QUEUES.UPLOAD_AVAILABILITY_FEED)
export class UploadAvailabilityFeedProcessor extends WorkerHost {
	private readonly logger = new Logger(UploadAvailabilityFeedProcessor.name)

	private readonly sftpClient: SFTPClient

	private restaurants: Company[] = []
	private settingsMap: Map<string, Settings> = new Map()
	private tablesMap: Map<string, Table[]> = new Map()
	private combinationsMap: Map<string, TableCombination[]> = new Map()

	constructor(
		private readonly _tableRepository: TableRepository,
		private readonly _companyRepository: CompanyRepository,
		private readonly _settingsRepository: SettingsRepository,
		private readonly _reservationRepository: ReservationRepository,
		private readonly _tableCombinationRepository: TableCombinationRepository,
	) {
		super()

		this.sftpClient = new SFTPClient()
	}

	@TracedProcessor()
	async process(job: UploadAvailabilityFeedJob): Promise<void> {
		const { restaurantIds } = job.data

		await this.uploadFeed(restaurantIds)
	}

	private async uploadFeed(restaurantIds: string[]): Promise<void> {
		try {
			await this.loadRestaurantData(restaurantIds)

			const feed = await this.generateFeed()
			const compressedFeed = await this.compressFeed(feed)

			await this.sftpClient.connect({
				host: GOOGLE.ACTION_CENTER.FEEDS.URL,
				port: GOOGLE.ACTION_CENTER.FEEDS.PORT,
				privateKey: GOOGLE.ACTION_CENTER.FEEDS.SFTP_KEY,
				username: GOOGLE.ACTION_CENTER.FEEDS.USERNAMES.AVAILABILITY,
			})

			await this.sftpClient.put(
				compressedFeed,
				`Availability_Feed_${Stage}_${feed.metadata?.generation_timestamp}.json.gz`,
			)

			this.logger.log({
				message: 'Availability feed uploaded',
				recordCount: feed.service_availability?.[0].availability?.length,
			})
		} catch (error) {
			this.logger.error({ message: 'Failed to upload Availability feed', error })
			throw error
		} finally {
			await this.sftpClient.end()
		}
	}

	private async generateFeed(): Promise<AvailabilityFeed> {
		const availabilities: Availability[] = []

		for (const restaurant of this.restaurants) {
			const settings = this.settingsMap.get(restaurant.id)

			if (!settings) {
				this.logger.warn({ message: 'Settings not found for tenant', tenantId: restaurant.id })
				continue
			}

			if (!settings.schedule) {
				this.logger.warn({ message: 'Schedule not set for tenant', tenantId: restaurant.id })
				continue
			}

			const reservations = await this._reservationRepository.findByInterval(
				new Date(),
				addWeeks(new Date(), GOOGLE.ACTION_CENTER.FEEDS.AVAILABILITY_FEED_WINDOW),
				{ where: { tenantId: restaurant.id, state: In(ApprovedOpenReservationFilter) }, relations: { tables: true } },
			)

			const availability = this.getAvailabilityForRestaurant(restaurant.id, settings.schedule, reservations)

			availabilities.push(...availability)
		}

		this.logTestSlots(availabilities)

		return {
			metadata: {
				processing_instruction: FeedMetadata_ProcessingInstruction.PROCESS_AS_COMPLETE,
				total_shards: 1,
				shard_number: 0,
				generation_timestamp: getUnixTime(new Date()).toString(),
			},
			service_availability: [{ availability: availabilities }],
		}
	}

	private getAvailabilityForRestaurant(
		restaurantId: string,
		schedule: ScheduleSettings,
		reservations: Reservation[],
	): Availability[] {
		const tables = this.tablesMap.get(restaurantId)
		if (!tables) {
			this.logger.warn({ message: 'No tables found for tenant', tenantId: restaurantId })
			return []
		}

		const combinations = this.combinationsMap.get(restaurantId) ?? []

		const timeSlots: Date[] = []
		const now = DateUtils.normalizeDay(new Date())

		// Generate timeslots for next N weeks, where N is the availability feed window
		for (const week of range(0, GOOGLE.ACTION_CENTER.FEEDS.AVAILABILITY_FEED_WINDOW - 1)) {
			const weekStart = addDays(now, week * 7)

			timeSlots.push(...getTimeSlotsForWeek(weekStart, schedule, DEFAULT_SLOT_LENGTH))
		}

		const availabilities: Availability[] = []

		const partySizes = [...getAllPartySizes(tables, combinations)]
		const duration_sec = (DEFAULT_SLOT_LENGTH * 60).toString()
		const service_id = getServiceId(restaurantId)

		for (const timeSlot of timeSlots) {
			// Find reservations overlapping this timeSlot
			const overlappingReservations = reservations.filter((reservation) =>
				areIntervalsOverlapping(
					{ start: reservation.startDate, end: reservation.endDate },
					{ start: timeSlot, end: addMinutes(timeSlot, DEFAULT_SLOT_LENGTH) },
				),
			)
			const start_sec = getUnixTime(timeSlot).toString()
			for (const [partySize, tableCount] of partySizes) {
				// Shared with BatchAvailabilityLookup so feed and booking server always agree
				const spotsOpen = getOpenTableCount(tables, overlappingReservations, partySize, combinations)

				if (spotsOpen < 0) continue

				availabilities.push({
					merchant_id: restaurantId,
					service_id,
					start_sec,
					duration_sec,
					spots_total: tableCount.toString(),
					spots_open: spotsOpen.toString(),
					resources: { party_size: partySize },
				})
			}
		}

		return availabilities
	}

	private async compressFeed<T>(feed: T): Promise<Buffer> {
		const jsonBuffer = Buffer.from(JSON.stringify(feed))
		return gzipAsync(jsonBuffer)
	}

	private async loadRestaurantData(restaurantIds: string[]): Promise<void> {
		this.restaurants = await this._companyRepository.find({
			where: { id: In(restaurantIds) },
		})

		const settings = await this._settingsRepository.find({
			where: { tenantId: In(restaurantIds) },
		})
		this.settingsMap = new Map(settings.map((setting) => [setting.tenantId as string, setting]))

		const tables = await this._tableRepository.find({
			where: { tenantId: In(restaurantIds) },
		})
		this.tablesMap = new Map(
			restaurantIds.map((restaurantId) => [restaurantId, tables.filter((table) => table.tenantId === restaurantId)]),
		)

		const combinations = await this._tableCombinationRepository.find({
			where: { tenantId: In(restaurantIds) },
			relations: { tables: true },
		})
		this.combinationsMap = new Map(
			restaurantIds.map((restaurantId) => [
				restaurantId,
				combinations.filter((combination) => combination.tenantId === restaurantId),
			]),
		)
	}

	private logTestSlots(availabilities: Availability[]): void {
		// Production merchant testing slots from Google case 05341814 (2026-05-20)
		const slots = [
			{
				merchant_id: '0b4e3373-c01e-4ab2-87b7-7e705ef06f8e',
				start_sec: '1781112600',
				party_size: 2,
			},
			{
				merchant_id: '110c840b-cf9c-4074-9d3d-484cb8172d5d',
				start_sec: '1781199000',
				party_size: 2,
			},
			{
				merchant_id: '3ef7232f-3112-4d08-a950-7fe1901c6355',
				start_sec: '1781112600',
				party_size: 2,
			},
		]

		for (const slot of slots) {
			const availability = availabilities.find(
				(availability) =>
					availability.merchant_id === slot.merchant_id &&
					availability.start_sec === slot.start_sec &&
					availability.resources?.party_size === slot.party_size,
			)

			if (availability) {
				this.logger.log({ message: 'Found testing slot', slot: availability })
			} else {
				this.logger.error({ message: 'Missing testing slot', slot })
			}
		}
	}
}
